import omit from 'lodash/omit';
import isPlainObject from 'lodash/isPlainObject';
import type {
  ABTVariables,
  DynamicTaskObjectModules,
  SimpleABTVariable,
} from 'src/components/lib/automation-module-registry/automation-module-registry';


export const ENTITY_TYPE_EVENT_MAP = {
  email_send: 'action_email_v2',
  chatbot_trigger: 'chatbot_action',
  chatbot_over_sms: 'chatbot_action',
  sms_send: 'action_sms',
  voicebot_trigger: 'action_voice_ai',
  survey_trigger: 'action_survey_v2',
  link_tracking: 'action_external_link_v2',
  whatsapp_send: 'action_whatsapp',
  voicebot_init: 'action_voice_ai_v2',
} as const;

function getActionMapping(
  currentPath: string,
  nonObjectKeysObj: SimpleABTVariable,
) {
  let nonObKeysObject = nonObjectKeysObj;
  const currentPathArray = currentPath.split('.');
  const keyName = currentPathArray[currentPathArray.length - 1];
  const patharray = currentPath.split('.');
  const fieldGroup = patharray.slice(0, -1).join('.');
  //we are adding fieldGroup which is the JSON path upto this point
  //so that when any action variable is selected, the correct json path for dsl meta can be resolved from variable picker.
  // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'key' does not exist in type 'SimpleABTVariable'.
  nonObKeysObject = {...nonObKeysObject, key: keyName, fieldGroup};
  if (nonObKeysObject.show_on_ui) {
    return [[`{{${currentPath}}}`, nonObKeysObject]];
  }
  const key = patharray[patharray.length - 1];
  const fieldPath = `{{${currentPath}}}`;
  nonObKeysObject = {...nonObKeysObject, fieldPath, fieldGroup};
  //note(vish): add both mapping and reverse mapping for normalizing and parsing
  return [[key, nonObKeysObject]];
}

function getActionVariableIdentifiers(obj: unknown, currentPath = '$') {
  const visitedObjects = new Set();

  function getActionVariableIdentifiersHelper(
    obj: unknown,
    currentPath: string,
  ) {
    let path = new Map();
    let utilMap = new Map();

    if (obj != null && typeof obj === 'object') {
      if (visitedObjects.has(obj)) {
        return {pickerVariables: path, utilVariables: utilMap};
      }
      visitedObjects.add(obj);

      const nonObjectKeys = Object.keys(obj).filter(
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
        (key) => !isPlainObject(obj[key]),
      );

      const nonObjectKeysObj = nonObjectKeys.reduce<Record<string, any>>(
        (acc, key) => {
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
          acc[key] = obj[key];
          return acc;
        },
        {},
      );

      const objectKeys = Object.keys(obj).filter((key) =>
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
        isPlainObject(obj[key]),
      );

      if (nonObjectKeys.length > 0) {
        // @ts-expect-error - TS2345 - Argument of type 'Record<string, any>' is not assignable to parameter of type 'SimpleABTVariable'.
        const item = getActionMapping(currentPath, nonObjectKeysObj);
        const itemCopy = [...item];
        // @ts-expect-error - TS2339 - Property 'startsWith' does not exist on type 'string | SimpleABTVariable'.
        if (itemCopy[0][0].startsWith('{')) {
          // @ts-expect-error - TS2769 - No overload matches this call.
          path = new Map([...path, ...new Map(itemCopy)]);
        } else {
          // @ts-expect-error - TS2345 - Argument of type 'string | SimpleABTVariable' is not assignable to parameter of type 'object'.
          if (Object.hasOwn(itemCopy[0][1], 'entity_type')) {
            // @ts-expect-error - TS2339 - Property 'entity_type' does not exist on type 'string | SimpleABTVariable'.
            const actionEntityType = itemCopy[0][1].entity_type ?? '';
            // @ts-expect-error - TS2339 - Property 'fieldGroup' does not exist on type 'string | SimpleABTVariable'.
            const fieldGroup = itemCopy[0][1].fieldGroup ?? '';
            itemCopy[0][0] = `${fieldGroup};${itemCopy[0][0]};${actionEntityType}`;
            // @ts-expect-error - TS2769 - No overload matches this call.
            utilMap = new Map([...utilMap, ...new Map(itemCopy)]);
          }
        }
      }

      if (objectKeys.length > 0) {
        for (const objectKey of objectKeys) {
          const newCurrentPath = `${currentPath}.${escapeVarNames(objectKey)}`;
          if (
            obj != null &&
            typeof obj === 'object' &&
            // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
            obj[objectKey] !== null
          ) {
            const result = getActionVariableIdentifiersHelper(
              // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
              obj[objectKey],
              newCurrentPath,
            );
            path = new Map([...path, ...result.pickerVariables]);
            utilMap = new Map([...utilMap, ...result.utilVariables]);
          }
        }
      }
    }

    return {pickerVariables: path, utilVariables: utilMap};
  }
  return getActionVariableIdentifiersHelper(obj, currentPath);
}

// Helper function to determine if a key should be treated as a leaf property
const shouldTreatAsLeaf = (obj: any, key: string): boolean => {
  // Note(Anant & Ram): If items_schema is under an array type, treat it as a leaf property
  if (obj.type === 'array' && key === 'items_schema') {
    return true;
  }
  // Otherwise, treat as leaf if it's not a plain object
  return !isPlainObject(obj[key]);
};

export function getVariableIdentifiers(
  obj: unknown,
  currentPath = '$',
  selectionLevelPath = '$',
) {
  //base no more properties field in the object
  let path = new Map();

  if (obj != null && typeof obj === 'object') {
    const nonObjectKeys = Object.keys(obj).filter((key) =>
      shouldTreatAsLeaf(obj, key),
    );

    const nonObjectKeysObj = nonObjectKeys.reduce<Record<string, any>>(
      (acc, key) => {
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
        acc[key] = obj[key];
        return acc;
      },
      {},
    );

    nonObjectKeysObj.selectionLevelPath = selectionLevelPath;

    // Check if this object has valid selection_text
    // @ts-expect-error - TS7053
    const selectionText = obj['selection_text'];
    const hasSelectionText =
      typeof selectionText === 'string' && selectionText.trim() !== '';

    const objectKeys = Object.keys(obj).filter(
      (key) => !shouldTreatAsLeaf(obj, key),
    );

    if (hasSelectionText && objectKeys.length > 0) {
      let options = new Map<string, unknown>();
      for (const objectKey of objectKeys) {
        const newCurrentPath = `${currentPath}.${escapeVarNames(objectKey)}`;
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
        if (obj != null && typeof obj === 'object' && obj[objectKey] !== null) {
          const optionPath = getVariableIdentifiers(
            // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
            obj[objectKey],
            newCurrentPath,
            currentPath,
          ); // sending old currentPath as selectionLevelPath is there is selection text.
          options = new Map([...options, ...optionPath]);
          // we are updating path for parent before traversing its child objectes- need to change this.
          path = new Map([...path, ...optionPath]);
        }
      }
      // selection options need to be added before updating path for parent.
      //nonObjectKeysObj.selectionOptions = options;
    }

    // we are updating path for parent before traversing its child objectes- need to change this.
    if (nonObjectKeys.length > 0) {
      path = new Map([
        ...path,
        ...new Map([
          [
            `{{${currentPath}}}`,
            {
              ...nonObjectKeysObj,
              selectionLevelPath: `{{${selectionLevelPath}}}`,
            },
          ],
        ]),
      ]);
    }

    if (!hasSelectionText && objectKeys.length > 0) {
      for (const objectKey of objectKeys) {
        const newCurrentPath = `${currentPath}.${escapeVarNames(objectKey)}`;
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
        if (obj != null && typeof obj === 'object' && obj[objectKey] !== null) {
          // If the current property is an object, recurse.
          path = new Map([
            ...path,
            ...getVariableIdentifiers(
              // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
              obj[objectKey],
              newCurrentPath,
              newCurrentPath,
            ),
          ]);
        }
      }
    }
  }

  return path;
}
/** this function exists because backend will provide `properties` field in
 * the nested object but will expect frontend to skip in the json path identifier
 */
export function skipFieldsRecursively(obj: unknown, propToRemove: string) {
  if (!isPlainObject(obj) || obj === null) {
    return obj;
  }
  let newObj = obj;
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
  if (typeof obj === 'object' && typeof obj[propToRemove] != null) {
    newObj = omit(obj, [propToRemove]);
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
    newObj = {...newObj, ...(obj[propToRemove] ?? {})};
  }
  if (typeof newObj === 'object') {
    return Object.keys(newObj).reduce<Record<string, any>>((acc, key) => {
      acc[key] = skipFieldsRecursively(
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
        typeof newObj === 'object' && newObj[key],
        propToRemove,
      );
      return acc;
    }, {});
  }
  return {};
}

// @ts-expect-error - TS7006 - Parameter 'originalMap' implicitly has an 'any' type.
function transformUtils(originalMap) {
  const transformedMap = new Map();
  // @ts-expect-error - TS7006 - Parameter 'value' implicitly has an 'any' type. | TS7006 - Parameter 'key' implicitly has an 'any' type.
  originalMap.forEach((value, key) => {
    // Split the key into its components
    const [fieldGroup, keyPart, actionPart] = key.split(';');
    if (!transformedMap.has(actionPart)) {
      transformedMap.set(actionPart, new Map());
    }
    const inMap = transformedMap.get(actionPart);
    if (inMap && inMap instanceof Map && !inMap.has(fieldGroup)) {
      inMap.set(fieldGroup, new Map());
    }
    const innerMap =
      inMap && inMap instanceof Map ? inMap.get(fieldGroup) : new Map();
    innerMap?.set(keyPart, value);
  });
  return transformedMap;
}

export function getActionVariables(data: unknown): ABTVariables {
  const dataWithPropertiesSkipped = skipFieldsRecursively(data, 'properties');
  const variableList = getActionVariableIdentifiers(dataWithPropertiesSkipped);
  variableList.utilVariables = transformUtils(variableList.utilVariables);
  // @ts-expect-error - TS2322 - Type '{ pickerVariables: Map<any, any>; utilVariables: Map<any, any>; }' is not assignable to type 'ABTVariables'.
  return variableList;
}

export function getAllVariables<T>(data: unknown): Map<string, T> {
  const dataWithPropertiesSkipped = skipFieldsRecursively(data, 'properties');
  const variableList = getVariableIdentifiers(dataWithPropertiesSkipped);
  return variableList;
}

export function processReqResVariables<T>(data: unknown): Map<string, T> {
  const dataWithPropertiesSkipped = skipFieldsRecursively(data, 'properties');
  const variableList = getVariableIdentifiers(dataWithPropertiesSkipped);
  return variableList;
}

export function escapeVarNames(varName: string): string {
  return varName.replace(/\./g, '\\.');
}

export const extractAttributeNameFromIdentifier = (str: string): string => {
  const regexPattern = /(?:[^\\.]|\\.)+(?=\}\}$)/;
  const matches = str.match(regexPattern);
  if (matches && matches[0]) {
    return matches[0].replace(/\\\./g, '.');
  }
  return '';
};

export const getSupportedTypes = (
  taskModules: DynamicTaskObjectModules,
): Array<{
  moduleKey: string;
  type: string;
  moduleName: string;
}> => {
  const supportedTypes: Array<{
    moduleKey: string;
    moduleName: string;
    type: string;
  }> = [];
  Object.keys(taskModules).forEach((moduleKey) => {
    supportedTypes.push({
      moduleKey,
      type: taskModules[moduleKey].type,
      moduleName: taskModules[moduleKey].display_name,
    });
  });
  return supportedTypes;
};

export function getActionMapEvents(
  supportedTypes: Array<{
    moduleKey: string;
    type: string;
    moduleName: string;
  }>,
  abtEvents: Map<string, SimpleABTVariable>,
): Map<string, Map<string, SimpleABTVariable>> {
  const supportedEntityTypes: Array<{
    entityTypeEvent: any;
    moduleKey: string;
    moduleName: string;
  }> = [];
  supportedTypes.forEach(({moduleKey, type, moduleName}) => {
    if (Object.keys(ENTITY_TYPE_EVENT_MAP).includes(type)) {
      supportedEntityTypes.push({
        moduleKey,
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly email_send: "action_email_v2"; readonly chatbot_trigger: "chatbot_action"; readonly chatbot_over_sms: "chatbot_action"; readonly sms_send: "action_sms"; readonly voicebot_trigger: "action_voice_ai"; readonly survey_trigger: "action_survey_v2"; readonly link_tracking: "action_external_link_v2"; readonly wh...'.
        entityTypeEvent: ENTITY_TYPE_EVENT_MAP[type],
        moduleName,
      });
    }
  });

  const moduleWiseActionMap = supportedEntityTypes.reduce(
    (acc, {moduleKey, entityTypeEvent, moduleName}) => {
      const resultMap = new Map();
      for (const [key, value] of abtEvents) {
        if (entityTypeEvent === value.entity_type) {
          resultMap.set(key, {
            ...value,
            moduleKey,
            moduleName,
          });
        }
      }
      if (resultMap.size > 0) {
        acc.set(moduleKey, resultMap);
      }
      return acc;
    },
    new Map(),
  );
  return moduleWiseActionMap;
}

export function extractLastParamFromJSONPath(jsonPath: string): string {
  if (!jsonPath) {
    return '';
  }
  const splitArray = jsonPath.split('.');
  return splitArray[splitArray.length - 1].replace('}}', '');
}
